<template>
    <div>
        <div
            v-for="(widgetGroup, index) in widgetGroupList"
            :key="`${index}-${widgetGroup.WidgetGroupID}`"
            :class="{
                'editable-widgets': editMode
            }"
            class="widget--group_section mb-3 relative"
        >
            <div class="widget--group_border" />

            <div class="divide-y divide-ui-lines">
                <div class="flex items-center gap-4 pt-3 pb-3 px-4">
                    <template v-if="editMode && (editedGroupId === widgetGroup.WidgetGroupID)">
                        <div class="w-full flex items-center justify-between gap-4">
                            <div>
                                <RedesignBaseInput
                                    v-model="widgetGroup.WidgetGroupTitle"
                                    data-testid="input-group-title"
                                />
                            </div>
                            <div
                                class="flex items-center gap-4"
                            >
                                <div class="flex-center">
                                    <RedesignBaseButton
                                        icon="PlusCircle"
                                        size="mini"
                                        type="soft"
                                        color="secondary"
                                        data-testid="button-add-widgets-edit"
                                        @click="emit('add-new-widget', widgetGroup)"
                                    >
                                        {{ $t('dashboard.addWidgets') }}
                                    </RedesignBaseButton>
                                </div>
                                <div class="flex items-center gap-2">
                                    <RedesignBaseButton
                                        type="outline"
                                        size="mini"
                                        color="secondary"
                                        data-testid="button-cancel-group-edit"
                                        @click="emit('on-cancel-edit-group', widgetGroup)"
                                    >
                                        {{ $t('common.cancel') }}
                                    </RedesignBaseButton>
                                    <RedesignBaseButton
                                        size="mini"
                                        color="primary"
                                        data-testid="button-apply-group-edit"
                                        @click="emit('save-dashboard')"
                                    >
                                        {{ $t('common.apply') }}
                                    </RedesignBaseButton>
                                </div>
                                <div class="flex-center gap-2">
                                    <RedesignBaseIconButton
                                        icon="Files"
                                        :size="22"
                                        data-testid="button-copy-group-edit"
                                        @click="emit('copy-group', widgetGroup)"
                                    />
                                    <RedesignBaseIconButton
                                        icon="IconDelete"
                                        color="destructive"
                                        :size="22"
                                        data-testid="button-delete-group-edit"
                                        :class="{'cursor-not-allowed !text-inactive-elements' : isLastGroup}"
                                        @click="handleRemoveClick(widgetGroup)"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="w-full flex justify-between items-center">
                            <div>
                                <h3
                                    :style="getStyles"
                                    class="font-semibold text-main-2xl text-default-text"
                                >
                                    {{ widgetGroup.WidgetGroupTitle }}
                                </h3>
                            </div>
                            <div class="flex items-center gap-4">
                                <div class="flex-center">
                                    <RedesignBaseButton
                                        icon="PlusCircle"
                                        size="mini"
                                        type="soft"
                                        color="secondary"
                                        data-testid="button-add-new-widgets"
                                        @click="emit('add-new-widget', widgetGroup)"
                                    >
                                        {{ $t('dashboard.addWidgets') }}
                                    </RedesignBaseButton>
                                </div>
                                <RedesignBaseButton
                                    icon="Pencil"
                                    size="mini"
                                    type="outline"
                                    color="secondary"
                                    data-testid="button-edit-mode"
                                    @click="emit('on-edit-widget-group', widgetGroup)"
                                >
                                    {{ $t('general.editMode') }}
                                </RedesignBaseButton>
                                <RedesignMenuPopover
                                    data-testid="group-menu-popover"
                                    :menu-items="getMenuItems(widgetGroup)"
                                />
                            </div>
                        </div>
                    </template>
                </div>

                <div class="px-2 py-4">
                    <WidgetList
                        :editable="editMode && (editedGroupId === widgetGroup.WidgetGroupID)"
                        :grid-edit-mode="editMode"
                        :widget-group="widgetGroup"
                        :layout-type="layoutType"
                        :preview-mode="previewMode"
                        @remove-widget="emit('remove-widget', $event)"
                        @update-widget="emit('update-widget', $event)"
                        @duplicate-widget="emit('duplicate-widget', $event)"
                        @add-widget="emit('add-new-widget', $event)"
                    />
                </div>
            </div>
        </div>
        <RedesignReorderWidgetGroups
            ref="reorderGroupsModalRef"
            @confirm="reorderGroupsConfirm"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import WidgetList, { RemoveWidgetEmitPayload, UpdateWidgetEmitPayload } from '@/components/Widgets/WidgetList.vue'
import { ExtendedWidgetGroupList, IDashboard, WidgetList as WidgetListType } from '@/types/dashboard'
import { widgetGroupTitleStyles } from '@/composables/globalData/useLayouts'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignMenuPopover from '@/components/RedesignComponents/Popover/RedesignMenuPopover.vue'
import i18n from '@/i18n'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import RedesignBaseIconButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseIconButton.vue'
import RedesignReorderWidgetGroups from '@/components/RedesignComponents/Modal/RedesignReorderWidgetGroups.vue'


/* Props */
const props = withDefaults(
    defineProps<{
        activeDashboardData?: IDashboard
        layoutType?: string
        editMode?: boolean
        editedGroupId?: number
        previewMode?: boolean
        widgetGroupList?: Array<ExtendedWidgetGroupList>
    }>(),
    {
        activeDashboardData: undefined,
        layoutType: '',
        editMode: false,
        editedGroupId: undefined,
        widgetGroupList: () => []
    }
)

/* Emit */
const emit = defineEmits<{
    (e: 'remove-widget', payload: RemoveWidgetEmitPayload): void
    (e: 'update-widget', payload: UpdateWidgetEmitPayload): void
    (e: 'duplicate-widget', payload: WidgetListType): void
    (e: 'save-dashboard'): void
    (e: 'on-edit-widget-group', payload: ExtendedWidgetGroupList): void
    (e: 'on-cancel-edit-group', payload: ExtendedWidgetGroupList): void
    (e: 'add-new-widget', payload: ExtendedWidgetGroupList): void
    (e: 'remove-group', payload: ExtendedWidgetGroupList): void
    (e: 'copy-group', payload: ExtendedWidgetGroupList): void
    (e: 'reorder-groups', payload: Array<ExtendedWidgetGroupList>): void
}>()

const reorderGroupsModalRef = ref<typeof RedesignReorderWidgetGroups>()

/* Computed */
const getStyles = computed(() => {
    return widgetGroupTitleStyles.value
})

const isLastGroup = computed(() => {
    return (props.activeDashboardData?.WidgetGroupList?.length ?? 0) === 1
})

const handleRemoveClick = (widgetGroup: ExtendedWidgetGroupList) => {
    if (isLastGroup.value) return
    emit('remove-group', widgetGroup)
}

const getMenuItems = (widgetGroup: ExtendedWidgetGroupList) => {
    return [
        {
            icon: 'Files',
            label: i18n.t('common.duplicate').toString(),
            action: () => {
                emit('copy-group', widgetGroup)
            }
        },
        {
            icon: 'ArrowUpDown',
            label: i18n.t('common.reorder').toString(),
            action: () => {
                onReorderGroups()
            }
        },
        {
            icon: 'IconDelete',
            iconClass: isLastGroup.value ? 'cursor-not-allowed !text-inactive-elements' : 'text-destructive-actions',
            label: i18n.t('common.delete').toString(),
            menuItemClass: isLastGroup.value ? 'cursor-not-allowed !text-inactive-elements' : '',
            action: () => {
                handleRemoveClick(widgetGroup)
            }
        }
    ]
}

function onReorderGroups () {
    reorderGroupsModalRef.value?.open(props.activeDashboardData?.WidgetGroupList)
}

function reorderGroupsConfirm (groups: Array<ExtendedWidgetGroupList>) {
    emit('reorder-groups', groups)
    reorderGroupsModalRef.value?.close()
}
</script>

<style lang="scss" scoped>
.widget--group_section {
    @apply border border-gray-650 rounded;
    border-top: none;

    .widget--group_border {
        @apply absolute bg-primary h-1;
        width: calc(100% + 2px);
        margin: auto -1px auto -1px;
        border-radius: 4px 4px 0 0;
    }
}
</style>
